import { useState } from 'react'
import { generatePath } from 'react-router'
import { Box, Button, Typography } from '@mui/material'
import { AppointmentProfile } from 'src/types'
import { useNavigation } from 'src/hooks'
import { BOOKING_STEPS } from 'src/pages/MUIBased/Appointments/hooks/useBookingFlow'
import { useClinicFiltersFromLocation } from 'src/flows/AppointmentBooking/useAvailableSlots'
import CardLink from 'src/components/MUIBased/CardLink'
import { useAppointmentEvents } from 'src/pages/MUIBased/Appointments/hooks/useAppointmentEvents'

interface OfferingsCardContainerProps {
    title: string
    offerings: AppointmentProfile[]
    setActiveOffering: (_offering: AppointmentProfile | undefined) => void
    tagName?: string
    limit?: number
}

const OfferingsCardContainer = ({
    title,
    offerings,
    setActiveOffering,
    tagName = '*', // Default to all tags
    limit
}: OfferingsCardContainerProps) => {
    const { trackAppointmentCardCtaClicked, trackSeeMoreOptionsClicked } =
        useAppointmentEvents()
    const { navigate } = useNavigation()
    const { locationFilters } = useClinicFiltersFromLocation()

    const [currentLimit, setCurrentLimit] = useState(limit || 0)

    const handleSeeMoreClick = () => {
        setCurrentLimit(0)
        trackSeeMoreOptionsClicked(tagName)
    }

    const onCardClick = (offering: AppointmentProfile) => {
        const careTypes = offering.careTypes.map(careType => careType.name)
        trackAppointmentCardCtaClicked(offering)
        if (
            careTypes.some(careType =>
                ['group_therapy', 'support_group'].includes(careType)
            )
        ) {
            navigate(
                `/book-appointment/profile/${offering.appointmentProfileUuid}/time-selection?backTo=/appointments`
            )
        } else if (offering.preBookingQuestion) {
            navigate(
                `/book-appointment/profile/${offering.appointmentProfileUuid}/pre-questions?backTo=/appointments`
            )
        } else {
            if (offering.clinicsWithOffering.length < 2) {
                navigate(
                    `${generatePath(BOOKING_STEPS.deeplink.path, {
                        appointmentProfileUuid: offering.appointmentProfileUuid
                    })}${
                        locationFilters
                            ? `?locations=${locationFilters}&backTo=/appointments`
                            : '?backTo=/appointments'
                    }`
                )
            } else {
                setActiveOffering(offering)
            }
        }
    }

    return (
        <Box sx={{ pb: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                {title}
            </Typography>
            {offerings.length === 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Box>
                        It looks like we don’t have a service that matches your
                        search. Try adjusting your search term, or explore other
                        care options available at Tia.
                    </Box>
                    <img src="https://assets.asktia.com/svgs/squiggle.svg" />
                </Box>
            )}
            {offerings.map((offering: AppointmentProfile, index: number) => {
                if (currentLimit && index >= currentLimit) {
                    return null
                }

                return (
                    <CardLink
                        key={offering.appointmentProfileUuid}
                        label={offering.label}
                        onClick={() => onCardClick(offering)}
                    />
                )
            })}
            {!!currentLimit && currentLimit < offerings.length && (
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleSeeMoreClick}
                    size="large"
                    sx={{ width: '100%' }}
                >
                    See More
                </Button>
            )}
        </Box>
    )
}

export default OfferingsCardContainer
